import React, { useState, useEffect, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import { GET, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

const QUICK = [
	{ id: 'today', label: 'Bugun' },
	{ id: 'week', label: '7 kun' },
	{ id: 'month', label: '30 kun' },
]

function getRange(id) {
	const now = new Date()
	const fmt = d => formatDate(d, 'yyyy-MM-dd')
	if (id === 'today') return { start: fmt(now), end: fmt(now) }
	if (id === 'week') { const d = new Date(now); d.setDate(d.getDate() - 6); return { start: fmt(d), end: fmt(now) } }
	if (id === 'month') { const d = new Date(now); d.setDate(d.getDate() - 29); return { start: fmt(d), end: fmt(now) } }
	return { start: fmt(now), end: fmt(now) }
}

function StatCard({ label, value, sub, color = '#4f46e5' }) {
	return (
		<div className="text-center p-3" style={{ borderRight: '1px solid #eee' }}>
			<div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
			<div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{label}</div>
			{sub && <div style={{ fontSize: 11, color: '#aaa' }}>{sub}</div>}
		</div>
	)
}

export default function DailyDashboard() {
	const now = new Date()
	const [activeFilter, setActiveFilter] = useState('week')
	const [startDate, setStartDate] = useState(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6))
	const [endDate, setEndDate] = useState(now)
	const [departments, setDepartments] = useState([])
	const [loading, setLoading] = useState(false)

	const load = useCallback(async (sd, ed) => {
		setLoading(true)
		const res = await GET('/daily-work/dashboard', {
			start_date: formatDate(sd, 'yyyy-MM-dd'),
			end_date: formatDate(ed, 'yyyy-MM-dd'),
		}, { loader: false })
		if (httpOk(res)) setDepartments(res.data)
		setLoading(false)
	}, [])

	useEffect(() => {
		load(startDate, endDate)
	}, [load]) // eslint-disable-line react-hooks/exhaustive-deps

	function applyQuick(id) {
		setActiveFilter(id)
		const { start, end } = getRange(id)
		const s = new Date(start), e = new Date(end)
		setStartDate(s)
		setEndDate(e)
		load(s, e)
	}

	function applyRefresh() {
		setActiveFilter(null)
		load(startDate, endDate)
	}

	const deptColors = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed']

	return (
		<div className="an-page">
			<div className="page-header d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">DASHBOARD kunlik ish</h4>
			</div>

			{/* Filter bar */}
			<div className="card mb-3">
				<div className="card-body py-2 px-3">
					<div className="d-flex align-items-center gap-2 flex-wrap">
						{QUICK.map(q => (
							<button key={q.id}
								className={`btn btn-sm ${activeFilter === q.id ? 'btn-primary' : 'btn-outline-secondary'}`}
								onClick={() => applyQuick(q.id)}>
								{q.label}
							</button>
						))}
						<div style={{ width: 1, height: 28, background: '#dee2e6', margin: '0 4px' }} />
						<div className="calendar-input" style={{ width: 140 }}>
							<DatePicker
								className="form-control form-control-sm"
								dateFormat="dd.MM.yyyy"
								selected={startDate}
								onChange={d => { if (d) setStartDate(d) }}
							/>
							<i className="uil uil-calendar-alt" />
						</div>
						<div className="calendar-input" style={{ width: 140 }}>
							<DatePicker
								className="form-control form-control-sm"
								dateFormat="dd.MM.yyyy"
								selected={endDate}
								onChange={d => { if (d) setEndDate(d) }}
							/>
							<i className="uil uil-calendar-alt" />
						</div>
						<button className="btn btn-sm btn-success" onClick={applyRefresh}>
							{loading
								? <span className="spinner-border spinner-border-sm" />
								: <><i className="uil uil-sync" /> Yangilash</>
							}
						</button>
					</div>
				</div>
			</div>

			{departments.length === 0 && !loading && (
				<div className="card">
					<div className="card-body text-center py-5 text-muted">
						<i className="uil-chart-bar" style={{ fontSize: 48, display: 'block', marginBottom: 12 }}></i>
						Tanlangan davr uchun ma'lumotlar yo'q
					</div>
				</div>
			)}

			{departments.length > 0 && (
				<div className="row g-3">
					{departments.map((dept, di) => {
						const color = deptColors[di % deptColors.length]
						return (
							<div key={dept.product_type_id} className="col-12">
								<div className="card" style={{ borderTop: `4px solid ${color}` }}>
									<div className="card-body">
										{/* Header row */}
										<div className="d-flex justify-content-between align-items-center mb-3">
											<h5 className="mb-0 fw-bold" style={{ color }}>
												<i className="uil-layer-group me-2"></i>
												{dept.product_type_name}
											</h5>
											<span className="badge" style={{ background: color, fontSize: 13 }}>
												{dept.log_count} ta yozuv
											</span>
										</div>

										{/* Stats row */}
										<div className="row g-0 mb-3" style={{ background: '#f8f9fa', borderRadius: 8, overflow: 'hidden' }}>
											<div className="col-4">
												<StatCard
													label="Jami kraska"
													value={Number(dept.total_paint_kg).toFixed(1) + ' kg'}
													color={color}
												/>
											</div>
											<div className="col-4">
												<StatCard
													label="O'rtacha ishchi/kun"
													value={dept.total_workers + ' ta'}
													color={color}
												/>
											</div>
											<div className="col-4">
												<StatCard
													label="Ish yozuvlari"
													value={dept.log_count}
													color={color}
												/>
											</div>
										</div>

										<div className="row g-3">
											{/* Products made */}
											<div className="col-md-5">
												<div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px' }}>
													<div className="fw-bold mb-2" style={{ fontSize: 13, color: '#555' }}>
														<i className="uil-box me-1"></i>Tayyorlangan mahsulotlar
													</div>
													{dept.items.length === 0 ? (
														<div className="text-muted" style={{ fontSize: 12 }}>Ma'lumot yo'q</div>
													) : (
														<table className="table table-sm mb-0" style={{ fontSize: 13 }}>
															<thead>
																<tr>
																	<th className="border-0 ps-0">Mahsulot</th>
																	<th className="border-0 text-end pe-0">Jami soni</th>
																</tr>
															</thead>
															<tbody>
																{dept.items.map((item, ii) => (
																	<tr key={ii}>
																		<td className="ps-0 border-0 py-1">{item.product_name}</td>
																		<td className="text-end pe-0 border-0 py-1 fw-bold">{item.total_quantity}</td>
																	</tr>
																))}
															</tbody>
														</table>
													)}
												</div>
											</div>

											{/* Workers — per brigadir submission */}
											<div className="col-md-4">
												<div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px' }}>
													<div className="fw-bold mb-2" style={{ fontSize: 13, color: '#555' }}>
														<i className="uil-users-alt me-1"></i>Hisobot berganlar
													</div>
													{dept.workers.map((w, wi) => (
														<div key={wi} className="d-flex justify-content-between align-items-center py-1"
															style={{ borderBottom: '1px solid #eee', fontSize: 13 }}>
															<div>
																<div>{w.worker_name || w.user_login}</div>
																<div className="text-muted" style={{ fontSize: 11 }}>
																	{w.total_workers} ta ishchi &middot; {w.log_count} ta yozuv
																</div>
															</div>
															<span className="badge" style={{ background: color }}>
																{Number(w.total_paint_kg).toFixed(1)} kg
															</span>
														</div>
													))}
												</div>
											</div>

											{/* Daily breakdown */}
											<div className="col-md-3">
												<div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px' }}>
													<div className="fw-bold mb-2" style={{ fontSize: 13, color: '#555' }}>
														<i className="uil-calendar-alt me-1"></i>Kunlik (so'nggi)
													</div>
													{dept.daily.slice(0, 7).map((d, di2) => (
														<div key={di2} className="d-flex justify-content-between align-items-center py-1"
															style={{ borderBottom: '1px solid #eee', fontSize: 12 }}>
															<div>
																<div className="text-muted">{formatDate(d.work_date, 'dd.MM.yyyy')}</div>
																<div style={{ fontSize: 11, color: '#aaa' }}>{d.total_workers} ta ishchi</div>
															</div>
															<span className="fw-bold">{Number(d.total_paint_kg).toFixed(1)} kg</span>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						)
					})}
				</div>
			)}
			</div>
	)
}
